import {Box} from "@chakra-ui/react";
import {useTranslation} from "react-i18next";
import {HiOutlineBriefcase, HiOutlineDocumentText} from "react-icons/hi2";
import {
    Field,
    FormInput,
    FormTextarea,
    FormSelect,
    FormCheckbox,
    Grid2,
    Grid3,
    CollapsibleSection,
    Divider,
    RepeatableText,
    AdditionalInfoField,
    isFieldInvalid,
    isFieldWarning,
    EmailTagsInput
} from "../ui";
import {useJobOfferEnums} from "@/models/jobOffer";

const Step1Offer = ({formik, warningErrors, visited, forceOpen}) => {
    const {t} = useTranslation("jobOffer");
    const {WORK_MODE, SENIORITY_LEVEL, OFFER_STATUS} = useJobOfferEnums();
    const {values, setFieldValue, handleChange} = formik;
    const basicInfo = values.basicInfo;
    const positionDescription = values.positionDescription;
    const invalid = (path) => isFieldInvalid(formik, path);
    const warn = (path) => isFieldWarning(warningErrors, visited, path);
    const plannedStartDateIsAsap = basicInfo.plannedStartDateIsAsap;
    const sectionInfo = values.sectionAdditionalInfo ?? {};
    const setSectionInfo = (key, val) => setFieldValue(`sectionAdditionalInfo.${key}`, val);

    return (
        <Box display="flex" flexDirection="column" gap={6}>
            <CollapsibleSection
                number={1}
                title={t("step1.basicInfo.title")}
                subtitle={t("step1.basicInfo.subtitle")}
                icon={HiOutlineBriefcase}
                defaultOpen
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={4}>
                    <Grid2>
                        <Field label={t("step1.basicInfo.positionName")} required
                               invalid={invalid("basicInfo.positionName")} warning={warn("basicInfo.positionName")}>
                            <FormInput
                                name="basicInfo.positionName"
                                value={basicInfo.positionName}
                                onChange={handleChange}
                                placeholder={t("step1.basicInfo.positionNamePlaceholder")}
                            />
                        </Field>
                        <Field label={t("step1.basicInfo.department")}>
                            <FormInput
                                name="basicInfo.department"
                                value={basicInfo.department}
                                onChange={handleChange}
                                placeholder={t("step1.basicInfo.departmentPlaceholder")}
                            />
                        </Field>
                    </Grid2>

                    <Grid2>
                        <Field label={t("step1.basicInfo.companyName")}>
                            <FormInput
                                name="basicInfo.companyName"
                                value={basicInfo.companyName}
                                onChange={handleChange}
                                placeholder={t("step1.basicInfo.companyNamePlaceholder")}
                            />
                        </Field>
                        <Field label={t("step1.basicInfo.location")} required invalid={invalid("basicInfo.location")}
                               warning={warn("basicInfo.location")}>
                            <FormInput
                                name="basicInfo.location"
                                value={basicInfo.location}
                                onChange={handleChange}
                                placeholder={t("step1.basicInfo.locationPlaceholder")}
                            />
                        </Field>
                    </Grid2>

                    <Field label={t("step1.basicInfo.companyDescription")} invalid={invalid("basicInfo.companyDescription")} warning={warn("basicInfo.companyDescription")}>
                        <FormTextarea
                            name="basicInfo.companyDescription"
                            value={basicInfo.companyDescription}
                            onChange={handleChange}
                            placeholder={t("step1.basicInfo.companyDescriptionPlaceholder")}
                            rows={3}
                        />
                    </Field>

                    <Grid3>
                        <Field label={t("step1.basicInfo.workMode")} required invalid={invalid("basicInfo.workMode")}
                               warning={warn("basicInfo.workMode")}>
                            <FormSelect
                                value={basicInfo.workMode}
                                onChange={(v) => setFieldValue("basicInfo.workMode", v)}
                                placeholder={t("step1.basicInfo.workModePlaceholder")}
                                options={WORK_MODE}
                            />
                        </Field>
                        <Field label={t("step1.basicInfo.vacancies")}>
                            <FormInput
                                type="number"
                                min="1"
                                name="basicInfo.vacancies"
                                value={basicInfo.vacancies ?? ""}
                                onChange={(e) => setFieldValue("basicInfo.vacancies", e.target.value === "" ? null : Number(e.target.value))}
                                placeholder="1"
                            />
                        </Field>
                        <Field label={t("step1.basicInfo.plannedStartDate")}>
                            <Box display="flex" alignItems="center" gap={3} w="full">
                                <Box flex={1} minW={0}>
                                    <FormInput
                                        type="date"
                                        name="basicInfo.plannedStartDate"
                                        value={plannedStartDateIsAsap ? "" : basicInfo.plannedStartDate}
                                        onChange={handleChange}
                                        disabled={plannedStartDateIsAsap}
                                        w="full"
                                    />
                                </Box>
                                <FormCheckbox
                                    label={t("step1.basicInfo.plannedStartDateAsap")}
                                    checked={plannedStartDateIsAsap}
                                    onChange={(checked) => {
                                        setFieldValue("basicInfo.plannedStartDateIsAsap", checked);
                                        if (checked) setFieldValue("basicInfo.plannedStartDate", "");
                                    }}
                                />
                            </Box>
                        </Field>
                    </Grid3>

                    <Grid2>
                        <Field label={t("step1.basicInfo.seniorityLevel")}>
                            <FormSelect
                                value={basicInfo.seniorityLevel}
                                onChange={(v) => setFieldValue("basicInfo.seniorityLevel", v)}
                                placeholder={t("step1.basicInfo.seniorityLevelPlaceholder")}
                                options={SENIORITY_LEVEL}
                            />
                        </Field>
                        <Field label={t("step1.basicInfo.offerStatus")} required
                               invalid={invalid("basicInfo.offerStatus")} warning={warn("basicInfo.offerStatus")}>
                            <FormSelect
                                value={basicInfo.offerStatus}
                                onChange={(v) => setFieldValue("basicInfo.offerStatus", v)}
                                options={OFFER_STATUS}
                            />
                        </Field>
                    </Grid2>

                    <Grid2>
                        <Box display="flex" flexDirection="column" gap={3}>
                            <FormCheckbox
                                label={t("step1.basicInfo.sendNotifications")}
                                checked={basicInfo.sendNotifications}
                                onChange={(checked) => {
                                    setFieldValue("basicInfo.sendNotifications", checked);
                                    if (!checked) setFieldValue("basicInfo.notificationEmails", []);
                                }}
                            />
                            {basicInfo.sendNotifications && (
                                <Field
                                    label={t("step1.basicInfo.notificationEmails")}
                                    required
                                    invalid={invalid("basicInfo.notificationEmails")}
                                    warning={warn("basicInfo.notificationEmails")}
                                >
                                    <EmailTagsInput
                                        values={basicInfo.notificationEmails}
                                        onChange={(emails) => setFieldValue("basicInfo.notificationEmails", emails)}
                                        placeholder={t("step1.basicInfo.notificationEmailsPlaceholder")}
                                    />
                                </Field>
                            )}
                        </Box>
                    </Grid2>
                    <AdditionalInfoField value={sectionInfo.basicInfo}
                                         onChange={(e) => setSectionInfo("basicInfo", e.target.value)}/>
                </Box>
            </CollapsibleSection>

            <CollapsibleSection
                number={2}
                title={t("step1.positionDescription.title")}
                subtitle={t("step1.positionDescription.subtitle")}
                icon={HiOutlineDocumentText}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={5}>
                    <Field label={t("step1.positionDescription.shortDescription")} required
                           invalid={invalid("positionDescription.shortDescription")}
                           warning={warn("positionDescription.shortDescription")}>
                        <FormTextarea
                            name="positionDescription.shortDescription"
                            value={positionDescription.shortDescription}
                            onChange={handleChange}
                            placeholder={t("step1.positionDescription.shortDescriptionPlaceholder")}
                            rows={3}
                        />
                    </Field>

                    <RepeatableText
                        label={t("step1.positionDescription.mainResponsibilities")}
                        required
                        invalid={invalid("positionDescription.mainResponsibilities")}
                        warning={warn("positionDescription.mainResponsibilities")}
                        values={positionDescription.mainResponsibilities}
                        onChange={(v) => setFieldValue("positionDescription.mainResponsibilities", v)}
                        placeholder={t("step1.positionDescription.mainResponsibilitiesPlaceholder")}
                        multiline
                    />

                    <RepeatableText
                        label={t("step1.positionDescription.responsibilitiesScope")}
                        values={positionDescription.responsibilitiesScope}
                        onChange={(v) => setFieldValue("positionDescription.responsibilitiesScope", v)}
                        placeholder={t("step1.positionDescription.responsibilitiesScopePlaceholder")}
                        multiline
                    />

                    <Grid2>
                        <Field label={t("step1.positionDescription.positionGoals")}>
                            <FormTextarea
                                name="positionDescription.positionGoals"
                                value={positionDescription.positionGoals}
                                onChange={handleChange}
                                placeholder={t("step1.positionDescription.positionGoalsPlaceholder")}
                                rows={2}
                            />
                        </Field>
                        <Field label={t("step1.positionDescription.typicalWorkday")}>
                            <FormTextarea
                                name="positionDescription.typicalWorkday"
                                value={positionDescription.typicalWorkday}
                                onChange={handleChange}
                                placeholder={t("step1.positionDescription.typicalWorkdayPlaceholder")}
                                rows={2}
                            />
                        </Field>
                    </Grid2>

                    <Grid2>
                        <Field label={t("step1.positionDescription.teamStructure")}>
                            <FormTextarea
                                name="positionDescription.teamStructure"
                                value={positionDescription.teamStructure}
                                onChange={handleChange}
                                placeholder={t("step1.positionDescription.teamStructurePlaceholder")}
                                rows={2}
                            />
                        </Field>
                        <Field label={t("step1.positionDescription.supervisor")}>
                            <FormInput
                                name="positionDescription.supervisor"
                                value={positionDescription.supervisor}
                                onChange={handleChange}
                                placeholder={t("step1.positionDescription.supervisorPlaceholder")}
                            />
                        </Field>
                    </Grid2>

                    <Divider/>

                    <RepeatableText
                        label={t("step1.positionDescription.toolsAndSystems")}
                        values={positionDescription.toolsAndSystems}
                        onChange={(v) => setFieldValue("positionDescription.toolsAndSystems", v)}
                        placeholder={t("step1.positionDescription.toolsAndSystemsPlaceholder")}
                    />

                    <RepeatableText
                        label={t("step1.positionDescription.benefits")}
                        values={positionDescription.benefits}
                        onChange={(v) => setFieldValue("positionDescription.benefits", v)}
                        placeholder={t("step1.positionDescription.benefitsPlaceholder")}
                    />

                    <RepeatableText
                        label={t("step1.positionDescription.recruitmentStages")}
                        values={positionDescription.recruitmentStages}
                        onChange={(v) => setFieldValue("positionDescription.recruitmentStages", v)}
                        placeholder={t("step1.positionDescription.recruitmentStagesPlaceholder")}
                    />

                    <Field label={t("step1.positionDescription.employeeOnboarding")} invalid={invalid("positionDescription.employeeOnboarding")} warning={warn("positionDescription.employeeOnboarding")}>
                        <FormTextarea
                            name="positionDescription.employeeOnboarding"
                            value={positionDescription.employeeOnboarding}
                            onChange={handleChange}
                            placeholder={t("step1.positionDescription.employeeOnboardingPlaceholder")}
                            rows={3}
                        />
                    </Field>

                    <AdditionalInfoField value={sectionInfo.positionDescription}
                                         onChange={(e) => setSectionInfo("positionDescription", e.target.value)}/>
                </Box>
            </CollapsibleSection>
        </Box>
    );
};

export default Step1Offer;
